<form [formGroup]="logicForm" (ngSubmit)="onSubmit()" novalidate>
    <nz-card nzTitle="Create a Pay Rule" [nzExtra]="extraTemplate">
      <p>Use this form to create rules for calculating pay based on project details.</p>
  
      <div formArrayName="conditions">
        <div *ngFor="let condition of conditions.controls; let i = index" [formGroupName]="i">
          <nz-divider *ngIf="i > 0" [nzText]="logicOperatorTemplate"></nz-divider>
  
          <ng-template #logicOperatorTemplate>
            <nz-select formControlName="logicOperator" class="full-width-control">
              <nz-option *ngFor="let op of logicOperators" [nzValue]="op.value" [nzLabel]="op.label"></nz-option>
            </nz-select>
          </ng-template>
  
          <div class="form-row">
            <div class="form-label">
              <nz-form-label nzRequired nzTooltipTitle="Select a project attribute to base your rule on">Project Attribute</nz-form-label>
            </div>
            <div class="form-control">
              <nz-select formControlName="condition" nzPlaceHolder="Select a project attribute" class="full-width-control">
                <nz-option *ngFor="let column of projectColumns" [nzValue]="column" [nzLabel]="column === 'user_id' ? 'Salesperson' : column"></nz-option>
              </nz-select>
            </div>
          </div>
  
          <div class="form-row">
            <div class="form-label">
              <nz-form-label nzRequired nzTooltipTitle="Choose how to compare the project attribute">Comparison</nz-form-label>
            </div>
            <div class="form-control">
              <nz-select formControlName="operator" nzPlaceHolder="Select a comparison" class="full-width-control">
                <nz-option *ngFor="let op of operators" [nzValue]="op.value" [nzLabel]="op.label"></nz-option>
              </nz-select>
            </div>
          </div>
  
          <div class="form-row" *ngIf="shouldShowComparisonValue(condition, i)">
            <div class="form-label">
              <nz-form-label nzRequired nzTooltipTitle="Enter the value to compare against">Comparison Value</nz-form-label>
            </div>
            <div class="form-control">
              <ng-container *ngIf="condition.get('operator')?.value === 'in' && condition.get('condition')?.value === 'user_id'; else defaultValueCtrl">
                <div style="display:flex; gap:8px; align-items:center; margin-bottom:8px;">
                  <span>Value type:</span>
                  <nz-radio-group [formControl]="condition.get('valueMode')">
                    <label nz-radio-button nzValue="users">Users</label>
                    <label nz-radio-button nzValue="groups">Groups</label>
                  </nz-radio-group>
                </div>
                <nz-select
                  *ngIf="condition.get('valueMode')?.value === 'users'"
                  formControlName="value"
                  nzMode="multiple"
                  nzPlaceHolder="Select users"
                  class="full-width-control"
                  [nzShowSearch]="true">
                  <nz-option *ngFor="let u of users" [nzLabel]="u.first_name + ' ' + u.last_name" [nzValue]="u.id"></nz-option>
                </nz-select>
                <nz-select
                  *ngIf="condition.get('valueMode')?.value === 'groups'"
                  formControlName="value"
                  nzMode="multiple"
                  nzPlaceHolder="Select groups"
                  class="full-width-control"
                  [nzShowSearch]="true">
                  <nz-option *ngFor="let g of groups" [nzLabel]="g.name" [nzValue]="g.id"></nz-option>
                </nz-select>
              </ng-container>
              <ng-template #defaultValueCtrl>
                <input
                  nz-input
                  formControlName="value"
                  placeholder="Enter a value"
                  class="full-width-control" />
              </ng-template>
            </div>
          </div>
  
          <button *ngIf="i > 0" nz-button nzType="default" (click)="removeCondition(i)">Remove Condition</button>
        </div>
      </div>
  
      <button nz-button nzType="dashed" (click)="addCondition()" style="width: 100%; margin-bottom: 16px;">
        <i nz-icon nzType="plus"></i> Add Condition
      </button>
  
      <nz-divider nzText="Then"></nz-divider>
  
      <div class="form-row">
        <div class="form-label">
          <nz-form-label nzRequired nzTooltipTitle="Choose between fixed amount or logic-based modification">Modification Type</nz-form-label>
        </div>
        <div class="form-control">
          <nz-radio-group formControlName="thenType">
            <label nz-radio-button nzValue="fixed">Fixed Amount</label>
            <label nz-radio-button nzValue="logic">Logic Based</label>
          </nz-radio-group>
        </div>
      </div>
  
      <ng-container *ngIf="logicForm.get('thenType')?.value === 'logic'">
        <div class="form-row">
          <div class="form-label">
            <nz-form-label nzRequired nzTooltipTitle="Select which pay attribute to modify">Pay Attribute</nz-form-label>
          </div>
          <div class="form-control">
            <nz-select formControlName="thenColumn" nzPlaceHolder="Select a pay attribute" class="full-width-control">
              <nz-option *ngFor="let column of projectColumns" [nzValue]="column" [nzLabel]="column === 'user_id' ? 'Salesperson' : column"></nz-option>
            </nz-select>
          </div>
        </div>
  
        <div class="form-row">
          <div class="form-label">
            <nz-form-label nzRequired nzTooltipTitle="Choose how to modify the pay attribute">Modification</nz-form-label>
          </div>
          <div class="form-control">
            <nz-select formControlName="thenOperator" nzPlaceHolder="Select a modification" class="full-width-control">
              <nz-option *ngFor="let op of arithmeticOperators" [nzValue]="op.value" [nzLabel]="op.label"></nz-option>
            </nz-select>
          </div>
        </div>
      </ng-container>
  
      <div class="form-row">
        <div class="form-label">
          <nz-form-label nzRequired nzTooltipTitle="Enter the value to modify by">
            {{logicForm.get('thenType')?.value === 'fixed' ? 'Fixed Amount' : 'Modification Value'}}
          </nz-form-label>
        </div>
        <div class="form-control">
          <input *ngIf="logicForm.get('thenType')?.value === 'fixed'"
            nz-input
            formControlName="thenValue"
            placeholder="Enter fixed amount"
            class="full-width-control" />
          <input *ngIf="logicForm.get('thenType')?.value === 'logic'"
            nz-input
            formControlName="thenValue"
            placeholder="Enter a value"
            class="full-width-control" />
        </div>
      </div>
  
      <div class="form-row">
        <div class="form-label">
          <nz-form-label nzTooltipTitle="Enable case-sensitive matching for text comparisons">
            Case Sensitive
          </nz-form-label>
        </div>
        <div class="form-control">
          <nz-switch formControlName="caseSensitive"></nz-switch>
        </div>
      </div>
  
      <button nz-button nzType="primary" [disabled]="!logicForm.valid">Add Rule</button>
    </nz-card>
  </form>
  
  <ng-template #extraTemplate>
    <a nz-tooltip nzTooltipTitle="Learn how to create pay rules" href="#" target="_blank">
      <i nz-icon nzType="question-circle" nzTheme="outline"></i>
    </a>
  </ng-template>
